import React, { FC, useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  useTheme, 
  Button as MuiButton,
  Grid2,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  Collapse,
  CircularProgress,
  Avatar,
  Chip,
  Card,
  CardContent,
  alpha
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { GoPlus } from "react-icons/go";
import { 
  MdEdit, 
  MdDeleteOutline, 
  MdExpandMore, 
  MdExpandLess, 
  MdClose, 
  MdPeopleAlt, 
  MdVerifiedUser, 
  MdAdminPanelSettings,
  MdOutlineSecurity
} from "react-icons/md";
import { useListCollaborators } from '../../Collaborators/List/collaborators-list.hook';
import { useCreateCollaborators } from '../../Collaborators/Form/create-collaborators.hook';
import { ListCollaboratorsUseCase } from '../../../Usecase/Collaborators/list-collaborators-usecase';
import { DestroyCollaboratorsUseCase } from '../../../Usecase/Collaborators/destroy-collaborators-usecase';
import { CreateCollaboratorsUseCase } from '../../../Usecase/Collaborators/create-collaborators-usecase';
import { ShowCollaboratorsUseCase } from '../../../Usecase/Collaborators/show-collaborators-usecase';
import { UpdateCollaboratorsUseCase } from '../../../Usecase/Collaborators/update-collaborators-usecase';
import { Table } from '../../../Components/Table/table.component';
import { Loader } from '../../../Components/Loader';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { Input } from '../../../Components/Form/Input/input.component';
import { Switch } from '../../../Components/Form/Switch/switch.component';
import { Button } from '../../../Components/Form/Button/button.scomponent';

interface ICollaboratorsHCPProps {
  listCollaboratorsUseCase: ListCollaboratorsUseCase;
  destroyCollaboratorsUseCase: DestroyCollaboratorsUseCase;
  createCollaboratorsUseCase: CreateCollaboratorsUseCase;
  showCollaboratorsUseCase: ShowCollaboratorsUseCase;
  updateCollaboratorsUseCase: UpdateCollaboratorsUseCase;
}

export const CollaboratorsHCP: FC<ICollaboratorsHCPProps> = (props) => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  // Hook de Listagem
  const { 
    listCollaborators, 
    list, 
    data: allData,
    loading: loadingList, 
    setCollaboratorToDestroy, 
    collaboratorToDestroy, 
    handleDestroyCollaborator, 
    totalRegisters 
  } = useListCollaborators({
    listCollaboratorsUseCase: props.listCollaboratorsUseCase,
    destroyCollaboratorsUseCase: props.destroyCollaboratorsUseCase,
    onlyHCP: true
  });

  // Hook de Cadastro/Edição - Usando o hook compartilhado com as novas funcionalidades
  const { 
    handleAction, 
    loading: loadingAction, 
    errors, 
    register, 
    control, 
    handleSubmit, 
    menuOptions, 
    expandedMenus, 
    toggleMenuExpansion,
    reset,
    setHCPDefaults,
    loadCollaborator
  } = useCreateCollaborators({
    createCollaboratorsUseCase: props.createCollaboratorsUseCase,
    showCollaboratorsUseCase: props.showCollaboratorsUseCase,
    updateCollaboratorsUseCase: props.updateCollaboratorsUseCase,
    id: editingId,
    onlyHCP: true
  });

  useEffect(() => {
    listCollaborators({ page: 1, search: '' });
  }, []);

  // Estatísticas para os cards
  const stats = useMemo(() => {
    const total = list?.length || 0;
    const hcpAccess = list?.filter(c => c.permissionsScreens?.some((p: string) => p.includes('hcp'))).length || 0;
    const stockAccess = list?.filter(c => c.permissionsScreens?.some((p: string) => p.includes('stocks'))).length || 0;
    
    return { total, hcpAccess, stockAccess };
  }, [list]);

  const handleOpenDrawer = (id?: string) => {
    reset();
    setEditingId(id);
    setDrawerOpen(true);
    if (id) {
      loadCollaborator(id);
    } else {
      // Para NOVOS colaboradores no HCP, já iniciamos com todas as permissões do HCP ativas
      setHCPDefaults();
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingId(undefined);
    reset();
  };

  const onSaveSuccess = async (values: any) => {
    await handleAction(values);
    handleCloseDrawer();
    listCollaborators({ page: 1, search: '' });
  };

  const getPermissionLabel = (perm: string) => {
    const p = perm.toLowerCase();
    if (p === 'all') return 'Admin';
    if (p.includes('hcp')) return 'HCP';
    if (p.includes('orders') || p.includes('pedidos')) return 'Pedidos';
    if (p.includes('stocks') || p.includes('estoque')) return 'Estoque';
    if (p.includes('production') || p.includes('produção')) return 'Produção';
    if (p.includes('financial') || p.includes('financeiro')) return 'Financeiro';
    if (p.includes('products') || p.includes('produtos')) return 'Produtos';
    if (p.includes('customers') || p.includes('clientes')) return 'Clientes';
    if (p.includes('boxes') || p.includes('caixas')) return 'Caixas';
    if (p.includes('sellers') || p.includes('vendedores')) return 'Vendedores';
    return null;
  };

  const columns: GridColDef[] = useMemo(() => [
    { 
      field: 'name', 
      headerName: 'Colaborador', 
      width: 400,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%', py: 1 }}>
          <Avatar sx={{ 
            width: 44,
            height: 44,
            bgcolor: alpha(theme.palette.primary.main, 0.1), 
            color: theme.palette.primary.main,
            fontWeight: 800,
            fontSize: '1rem',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.05)}`
          }}>
            {params.row.name?.substring(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ color: 'text.primary', lineHeight: 1.1, mb: 0.3 }}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: 0.2 }}>
              {params.row.username}
            </Typography>
          </Box>
        </Box>
      )
    },
    { 
      field: 'permissionsScreens', 
      headerName: 'Acessos Principais', 
      width: 500,
      renderCell: (params) => {
        const permsScreens = (params.row.permissionsScreens as string[]) || [];
        const permsKeys = (params.row.permissions as string[]) || [];
        // Combinar os dois arrays de permissão do Papello
        const allPerms = [...permsScreens, ...permsKeys];
        
        const labels = Array.from(new Set(allPerms.map(getPermissionLabel).filter(Boolean)));
        
        if (labels.length === 0) return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Nenhum acesso definido</Typography>
          </Box>
        );

        return (
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', alignItems: 'center', height: '100%', py: 1 }}>
            {labels.map((label: any) => {
              const isAdmin = label === 'Admin';
              return (
                <Chip 
                  key={label} 
                  label={label} 
                  size="small" 
                  sx={{ 
                    height: 22, 
                    fontSize: '0.65rem', 
                    fontWeight: 800,
                    bgcolor: isAdmin ? alpha(theme.palette.secondary.main, 0.1) : alpha(theme.palette.primary.main, 0.08),
                    color: isAdmin ? theme.palette.secondary.dark : theme.palette.primary.main,
                    border: `1px solid ${alpha(isAdmin ? theme.palette.secondary.main : theme.palette.primary.main, 0.2)}`,
                    '& .MuiChip-label': { px: 1 }
                  }} 
                />
              );
            })}
          </Box>
        );
      }
    },
    {
      field: "actions",
      headerName: "Gestão",
      headerAlign: "right",
      align: "right",
      width: 150,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
          <Tooltip title="Editar Permissões">
            <IconButton 
              size="small" 
              onClick={() => handleOpenDrawer(params.row.id)}
              sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.01), border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.05), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
            >
              <MdEdit size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => setCollaboratorToDestroy(params.row.id)}
              sx={{ bgcolor: alpha(theme.palette.error.main, 0.01), border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.05), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
            >
              <MdDeleteOutline size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ], [theme]);

  const SummaryCard = ({ title, value, icon, color }: { title: string, value: number, icon: any, color: string }) => (
    <Card sx={{ 
      flex: 1, 
      borderRadius: '16px', 
      boxShadow: 'none', 
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      background: `linear-gradient(135deg, #fff 0%, ${alpha(color, 0.02)} 100%)`,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ 
          position: 'absolute', 
          right: -10, 
          bottom: -10, 
          opacity: 0.05, 
          transform: 'scale(1.5)',
          color: color
        }}>
          {icon}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 44, height: 44 }}>
            {icon}
          </Avatar>
        </Box>
        <Typography variant="h4" fontWeight={900} sx={{ color: 'text.primary', mb: 0.5 }}>{value}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 1 }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 4, maxWidth: 1600, mx: 'auto' }}>
      <ConfirmDialog 
        title='Remover Colaborador' 
        onCancel={() => setCollaboratorToDestroy('')} 
        description='Você tem certeza que deseja remover este colaborador? O acesso ao sistema será revogado imediatamente.' 
        open={!!collaboratorToDestroy} 
        onConfirm={handleDestroyCollaborator}  
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 5 }}>
        <Box>
          <Typography variant="h3" fontWeight={900} color="text.primary" sx={{ letterSpacing: -1.5, mb: 0.5 }}>Equipe & Acessos</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Gerencie os colaboradores da Papello e controle permissões de produção e administrativas.
          </Typography>
        </Box>
        <MuiButton 
          variant="contained" 
          startIcon={<GoPlus size={24} />} 
          onClick={() => handleOpenDrawer()}
          sx={{ 
            borderRadius: '14px', 
            px: 4, 
            py: 1.8, 
            textTransform: 'none', 
            fontWeight: 800,
            fontSize: '1rem',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
            '&:hover': {
              boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.2s'
          }}
        >
          Novo Colaborador
        </MuiButton>
      </Box>

      {/* Cards de Indicadores */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
        <SummaryCard 
          title="Total de Colaboradores" 
          value={stats.total} 
          icon={<MdPeopleAlt size={32} />} 
          color={theme.palette.primary.main} 
        />
        <SummaryCard 
          title="Acesso ao Hub (HCP)" 
          value={stats.hcpAccess} 
          icon={<MdVerifiedUser size={32} />} 
          color="#2e7d32" 
        />
        <SummaryCard 
          title="Permissões de Estoque" 
          value={stats.stockAccess} 
          icon={<MdOutlineSecurity size={32} />} 
          color="#ed6c02" 
        />
      </Box>

      <Paper sx={{ 
        borderRadius: '24px', 
        overflow: 'hidden', 
        boxShadow: '0 4px 40px rgba(0,0,0,0.04)', 
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        bgcolor: '#fff'
      }}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`, bgcolor: alpha(theme.palette.primary.main, 0.01) }}>
          <Typography variant="subtitle1" fontWeight={800} color="text.primary">Listagem de Colaboradores</Typography>
        </Box>
        {loadingList && <Loader />}
        <Box sx={{ p: 1 }}>
          <Table data={list || []} columns={columns} totalCount={totalRegisters || 0} />
        </Box>
      </Paper>

      {/* Drawer de Cadastro/Edição */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 550 }, p: 0, display: 'flex', flexDirection: 'column', bgcolor: '#fcfcfc' }
        }}
      >
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.1) }}>
          <Box>
            <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: -0.5 }}>{editingId ? 'Ficha do Colaborador' : 'Adicionar Colaborador'}</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>Defina dados básicos e permissões de sistema</Typography>
          </Box>
          <IconButton onClick={handleCloseDrawer} sx={{ bgcolor: alpha(theme.palette.divider, 0.1) }}>
            <MdClose />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }} component="form" id="collaborator-form" onSubmit={handleSubmit(onSaveSuccess)}>
          {loadingAction && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}

          <Grid2 container spacing={4}>
            <Grid2 size={12}>
              <Typography variant="caption" fontWeight={800} color="primary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 2, display: 'block' }}>Dados de Identificação</Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <Input label="Nome Completo" placeholder="Ex: João Silva" error={!!errors.name} helperText={errors.name?.message} {...register('name')} fullWidth />
                </Grid2>
                <Grid2 size={12}>
                  <Input label="E-mail / Usuário" placeholder="joao@papello.com.br" error={!!errors.username} helperText={errors.username?.message} {...register('username')} fullWidth />
                </Grid2>
              </Grid2>
            </Grid2>

            <Grid2 size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" fontWeight={800} color="primary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 3, display: 'block' }}>Matriz de Permissões</Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {menuOptions?.map((menu: any) => (
                  <Box key={menu.value} sx={{ 
                    border: '1px solid', 
                    transition: 'all 0.2s',
                    borderColor: expandedMenus[menu.value] ? theme.palette.primary.main : alpha(theme.palette.divider, 0.15), 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    boxShadow: expandedMenus[menu.value] ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}` : 'none',
                    backgroundColor: expandedMenus[menu.value] ? '#fff' : 'transparent'
                  }}>
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Switch label={menu.label} name={`swtich-${menu.value}`} control={control} />
                      </Box>
                      {menu.features.length > 0 && (
                        <IconButton 
                          size="small" 
                          onClick={() => toggleMenuExpansion(menu.value)}
                          sx={{ bgcolor: alpha(theme.palette.divider, 0.05) }}
                        >
                          {expandedMenus[menu.value] ? <MdExpandLess /> : <MdExpandMore />}
                        </IconButton>
                      )}
                    </Box>

                    {menu.features.length > 0 && (
                      <Collapse in={expandedMenus[menu.value]}>
                        <Box sx={{ pl: 6.5, pr: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.1)}`, pt: 2 }}>
                          {menu.features.map((feature: any) => (
                            <Box key={feature.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Switch label={feature.label} name={`swtich-${feature.value}`} control={control} />
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid2>
          </Grid2>
        </Box>

        <Box sx={{ p: 4, borderTop: '1px solid', borderColor: alpha(theme.palette.divider, 0.1), backgroundColor: '#fff', boxShadow: '0 -4px 20px rgba(0,0,0,0.03)' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MuiButton variant="outlined" fullWidth onClick={handleCloseDrawer} sx={{ borderRadius: '12px', py: 1.5, textTransform: 'none', fontWeight: 700 }}>Cancelar</MuiButton>
            <MuiButton 
              type="submit" 
              variant="contained" 
              form="collaborator-form" 
              fullWidth 
              disabled={loadingAction}
              sx={{ borderRadius: '12px', py: 1.5, textTransform: 'none', fontWeight: 800 }}
            >
              {loadingAction ? <CircularProgress size={24} color="inherit" /> : (editingId ? 'SALVAR ALTERAÇÕES' : 'CRIAR COLABORADOR')}
            </MuiButton>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};
